import { createChart, CrosshairMode, TickMarkType } from 'lightweight-charts'
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { merge } from 'lodash'

import './Chart.scss'

const CHART_LOCALE = 'uk-UA'
const CHART_TIME_ZONE = 'Europe/Kyiv'
const DEFAULT_NAVIGATION_BAR_COUNT = 50
const DEFAULT_BAR_SPACING = 12
const MAX_BAR_SPACING = 56
const ZOOM_STEP_FACTOR = 1.25
const MIN_CHART_HEIGHT = 140
const EMPTY_OPTIONS = {}
const NOOP = () => { }
const chartTimeFormatterCache = new Map()
const CHART_DATE_TIME_FORMAT_OPTIONS = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
}

const getChartDate = (time) => {
  if (typeof time === 'number') {
    return new Date(time * 1000)
  }

  if (typeof time === 'string') {
    return new Date(`${time}T00:00:00Z`)
  }

  return new Date(Date.UTC(time.year, time.month - 1, time.day))
}

const getChartFormatter = (formatOptions) => {
  const formatterKey = JSON.stringify(formatOptions)

  if (!chartTimeFormatterCache.has(formatterKey)) {
    chartTimeFormatterCache.set(formatterKey, new Intl.DateTimeFormat(CHART_LOCALE, {
      ...formatOptions,
      timeZone: CHART_TIME_ZONE,
      hour12: false,
      hourCycle: 'h23'
    }))
  }

  return chartTimeFormatterCache.get(formatterKey)
}

const getChartDateParts = (time) => {
  return getChartFormatter(CHART_DATE_TIME_FORMAT_OPTIONS)
    .formatToParts(getChartDate(time))
    .reduce((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = part.value
      }

      return acc
    }, {})
}

const formatCrosshairTime = (time) => {
  const { day, month, year, hour, minute } = getChartDateParts(time)

  return `${day}.${month}.${year} ${hour}:${minute}`
}

const getTickMarkLabel = (time, tickMarkType) => {
  const { day, month, year, hour, minute, second } = getChartDateParts(time)

  switch (tickMarkType) {
    case TickMarkType.Year:
      return year
    case TickMarkType.Month:
      return `${month}.${year}`
    case TickMarkType.DayOfMonth:
      return `${day}.${month}`
    case TickMarkType.TimeWithSeconds:
      return `${hour}:${minute}:${second}`
    case TickMarkType.Time:
    default:
      return `${hour}:${minute}`
  }
}

const applyChartSize = (chart, width, height) => {
  const timeScale = chart.timeScale()
  const currentBarSpacing = timeScale.options().barSpacing
  const currentScrollPosition = timeScale.scrollPosition()

  chart.applyOptions({ width, height })

  if (typeof currentBarSpacing === 'number') {
    timeScale.applyOptions({ barSpacing: currentBarSpacing })
  }

  timeScale.scrollToPosition(currentScrollPosition, false)
}

export const Chart = forwardRef(({
  crosshairMove = NOOP,
  series,
  logicalRangeChange = NOOP,
  name = '',
  options = EMPTY_OPTIONS,
  chartHeightCoef = null,
  showNavigationControls = false
}, { chartsRef }) => {
  const chartContainerRef = useRef()
  const chartRef = useRef(null)
  const resizeStateRef = useRef(null)
  const isManualHeightRef = useRef(false)
  const [isSticky, setIsSticky] = useState(showNavigationControls)
  const getDefaultChartHeight = useCallback(() => {
    return chartHeightCoef ? window.innerHeight * chartHeightCoef - chartHeightCoef : options.height || 240
  }, [chartHeightCoef, options.height])
  const clampChartHeight = useCallback((nextHeight) => {
    const maxChartHeight = Math.max(MIN_CHART_HEIGHT, window.innerHeight - 120)

    return Math.min(Math.max(Math.round(nextHeight), MIN_CHART_HEIGHT), maxChartHeight)
  }, [])
  const [chartHeight, setChartHeight] = useState(() => clampChartHeight(getDefaultChartHeight()))
  const chartHeightRef = useRef(chartHeight)
  const lastLogicalIndex = useMemo(() => {
    return series.reduce((maxIndex, seriesItem) => {
      return Math.max(maxIndex, (seriesItem?.seriesData?.length ?? 0) - 1)
    }, -1)
  }, [series])

  const handlePointerMove = useCallback((event) => {
    if (!resizeStateRef.current) {
      return
    }

    const nextHeight = resizeStateRef.current.startHeight + (event.clientY - resizeStateRef.current.startY)

    setChartHeight(clampChartHeight(nextHeight))
  }, [clampChartHeight])

  const stopResize = useCallback(() => {
    resizeStateRef.current = null
    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', stopResize)
    window.removeEventListener('pointercancel', stopResize)
  }, [handlePointerMove])

  const startResize = useCallback((event) => {
    event.preventDefault()

    isManualHeightRef.current = true
    resizeStateRef.current = {
      startHeight: chartHeight,
      startY: event.clientY
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', stopResize)
    window.addEventListener('pointercancel', stopResize)
  }, [chartHeight, handlePointerMove, stopResize])

  const resetChartHeight = useCallback(() => {
    isManualHeightRef.current = false
    setChartHeight(clampChartHeight(getDefaultChartHeight()))
  }, [clampChartHeight, getDefaultChartHeight])

  const crosshairMoveHandler = useCallback((params) => {
    crosshairMove && crosshairMove(params)
  }, [crosshairMove])

  const logicalRangeChangeHandler = useCallback((logicalRange) => {
    logicalRangeChange && logicalRangeChange(logicalRange)
  }, [logicalRangeChange])

  useEffect(() => {
    chartHeightRef.current = chartHeight
  }, [chartHeight])

  useEffect(() => {
    return () => {
      stopResize()
    }
  }, [stopResize])

  const runChartAction = useCallback((action) => {
    const chart = chartRef.current

    if (!chart || lastLogicalIndex < 0) {
      return
    }

    const timeScale = chart.timeScale()

    switch (action) {
      case 'fit':
        timeScale.fitContent()
        break
      case 'latest':
        timeScale.scrollToRealTime()
        break
      case 'reset':
        timeScale.resetTimeScale()
        break
      default:
        break
    }
  }, [lastLogicalIndex])

  const zoomChart = useCallback((direction) => {
    const chart = chartRef.current

    if (!chart || lastLogicalIndex < 0) {
      return
    }

    const timeScale = chart.timeScale()
    const timeScaleOptions = timeScale.options()
    const currentBarSpacing = timeScaleOptions.barSpacing ?? DEFAULT_BAR_SPACING
    const minBarSpacing = Math.max(timeScaleOptions.minBarSpacing ?? 0.5, 0.5)
    const nextBarSpacing = direction === 'in'
      ? Math.min(currentBarSpacing * ZOOM_STEP_FACTOR, MAX_BAR_SPACING)
      : Math.max(currentBarSpacing / ZOOM_STEP_FACTOR, minBarSpacing)

    timeScale.applyOptions({
      barSpacing: nextBarSpacing
    })
  }, [lastLogicalIndex])

  const navigateChart = useCallback((target) => {
    const chart = chartRef.current

    if (!chart || lastLogicalIndex < 0) {
      return
    }

    const visibleLogicalRange = chart.timeScale().getVisibleLogicalRange()
    const visibleBars = Math.max(
      (visibleLogicalRange?.to ?? Math.min(lastLogicalIndex, DEFAULT_NAVIGATION_BAR_COUNT)) -
      (visibleLogicalRange?.from ?? 0),
      1
    )

    chart.timeScale().setVisibleLogicalRange(target === 'start'
      ? {
        from: -5,
        to: Math.min(lastLogicalIndex, visibleBars)
      }
      : {
        from: Math.max(lastLogicalIndex - visibleBars, 0),
        to: lastLogicalIndex + 5
      })
  }, [lastLogicalIndex])

  const chartControls = [
    {
      key: 'start',
      label: '<<',
      title: 'Go to the beginning of the chart',
      onClick: () => navigateChart('start')
    },
    {
      key: 'zoom-out',
      label: '-',
      title: 'Zoom out',
      onClick: () => zoomChart('out')
    },
    {
      key: 'fit',
      label: 'Fit',
      title: 'Fit all chart data',
      onClick: () => runChartAction('fit')
    },
    {
      key: 'reset',
      label: 'Reset',
      title: 'Reset zoom and scroll position',
      onClick: () => runChartAction('reset')
    },
    {
      key: 'sticky',
      label: 'Sticky',
      isActive: isSticky,
      title: `${isSticky ? 'Disable' : 'Enable'} sticky chart position`,
      onClick: () => setIsSticky((currentValue) => !currentValue)
    },
    {
      key: 'latest',
      label: 'Now',
      hidden: true,
      title: 'Scroll to the latest bar',
      onClick: () => runChartAction('latest')
    },
    {
      key: 'zoom-in',
      label: '+',
      title: 'Zoom in',
      onClick: () => zoomChart('in')
    },
    {
      key: 'end',
      label: '>>',
      title: 'Go to the end of the chart',
      onClick: () => navigateChart('end')
    }
  ]

  useEffect(
    () => {
      const handleResize = () => {
        const nextChartHeight = isManualHeightRef.current
          ? clampChartHeight(chartHeightRef.current)
          : clampChartHeight(getDefaultChartHeight())

        if (nextChartHeight !== chartHeightRef.current) {
          setChartHeight(nextChartHeight)
        }

        applyChartSize(chart, chartContainerRef.current.clientWidth, nextChartHeight)
      }
      const chartOptions = merge({
        height: chartHeight,
        layout: {
          background: { color: '#222' },
          textColor: '#DDD',
          fontFamily: 'Courier New'
        },
        grid: {
          vertLines: { color: '#333' },
          horzLines: { color: '#333' },
        },
        crosshair: {
          mode: CrosshairMode.Normal,
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          tickMarkFormatter: (time, tickMarkType) => getTickMarkLabel(time, tickMarkType)
        },
        localization: {
          timeFormatter: (time) => formatCrosshairTime(time),
          priceFormatter: (price) => {
            const integerPart = String(parseInt(price)).length
            const floatPart = 6 - integerPart

            return parseFloat(price).toFixed(floatPart)
          }
        },
        priceScale: {
          autoScale: true
        }
      }, options)

      const chart = createChart(chartContainerRef.current, chartOptions)

      chartRef.current = chart
      chart.timeScale().applyOptions({
        barSpacing: 12
      })

      const chartSeries = []

      series.forEach((seriesItem) => {
        if (!seriesItem) return

        const seriesOptions = merge({
          lastValueVisible: false,
          priceLineVisible: false,
        }, seriesItem.options)

        const newSeries = chart[`add${seriesItem.type}Series`](seriesOptions)

        if (seriesItem.priceLines) {
          seriesItem.priceLines.forEach((priceLine) => {
            newSeries.createPriceLine(priceLine)
          })
        }

        if (seriesItem.markers) {
          newSeries.setMarkers(seriesItem.markers)
        }

        newSeries.setData(seriesItem.seriesData)
        chartSeries.push({ series: newSeries, name: seriesItem.name, options: seriesItem.options })
      })

      if (chartsRef.current) {
        chartsRef.current.push({ chart, chartSeries, chartContainerRef })
      }

      window.addEventListener('resize', handleResize)
      chart.timeScale().subscribeVisibleLogicalRangeChange(logicalRangeChangeHandler)
      chart.subscribeCrosshairMove(crosshairMoveHandler)

      return () => {
        window.removeEventListener('resize', handleResize)
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(logicalRangeChangeHandler)
        chart.unsubscribeCrosshairMove(crosshairMoveHandler)

        chartsRef.current = chartsRef.current.filter(item => item.chart !== chart)

        if (chartRef.current === chart) {
          chartRef.current = null
        }

        chart.remove()
      }
    },
    [
      chartsRef,
      clampChartHeight,
      crosshairMove,
      crosshairMoveHandler,
      getDefaultChartHeight,
      logicalRangeChange,
      logicalRangeChangeHandler,
      options,
      series
    ]
  )

  useEffect(() => {
    const chart = chartRef.current

    if (!chart || !chartContainerRef.current) {
      return
    }

    applyChartSize(chart, chartContainerRef.current.clientWidth, chartHeight)
  }, [chartHeight])

  return (
    <div
      className={`chart${isSticky ? ' chart-sticky' : ''}`}
      style={{ height: chartHeight }}
      ref={chartContainerRef}>
      {showNavigationControls &&
        <div
          aria-label="Chart navigation controls"
          className="chart-navigation"
          role="toolbar">
          {chartControls.map(({ key, label, onClick, title, hidden, isActive = false }) => (
            !hidden && <button
              aria-label={title}
              aria-pressed={isActive}
              className={`chart-navigation-button${isActive ? ' chart-navigation-button-active' : ''}`}
              disabled={lastLogicalIndex < 0}
              key={key}
              onClick={onClick}
              title={title}
              type="button">
              {label}
            </button>
          ))}
        </div>}
      <div className="chart-legend">{name}</div>
      <button
        aria-label="Resize chart vertically"
        className="chart-resize-handle"
        onDoubleClick={resetChartHeight}
        onPointerDown={startResize}
        title="Drag to resize vertically. Double-click to reset."
        type="button">
        <span className="chart-resize-grip" />
      </button>
    </div>
  )
})

Chart.displayName = 'Chart'
Chart.propTypes = {
  crosshairMove: PropTypes.func,
  series: PropTypes.arrayOf(PropTypes.shape({})),
  logicalRangeChange: PropTypes.func,
  name: PropTypes.string,
  options: PropTypes.shape({}),
  chartHeightCoef: PropTypes.number,
  showNavigationControls: PropTypes.bool,
}


export default Chart
